import React, { useState, useMemo } from 'react';
import moment from 'moment';

import Tags from '../Tags';
import { colors } from 'global/colors';
import { Button } from '../index';
import { getConfig } from 'functions/getConfig';
import { userRoles, categories } from 'util/appConstants';
import YesNoModal from 'components/Modals/yesNoModal';
import {
  CardContainer,
  Image,
  ChallengeInfo,
  ChallengeName,
  ChallengeInfoHeader,
  ChallengeDetails,
  ChallengeCategory,
  ChallengeNameAndCategory,
  ButtonContainer,
  ChallengeDetailsAndTestTag,
  DetailsContainer,
  DetailsLabel,
  AnchorLink,
} from './styles';

export default function CustomTestCard({ test, user, publishOrArchiveTest, copyTest }) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({});

  const getEditedAtTime = () => {
    let time = test.editedAt ? test.editedAt : test.createdAt;
    let ago;
    let date = moment(time)._d;
    if (!time) {
      ago = '-';
    } else {
      ago = moment(date).fromNow();
    }
    return ago;
  };

  const isCopyTestVisible = () => {
    if (user.role === userRoles.SUPER_ADMIN && test.company === user.adminCompany) {
      return true;
    } else if (
      [userRoles.COMPANY_ADMIN, userRoles.DEPARTMENT_ADMIN, userRoles.EMPLOYEE].includes(user.role) &&
      test.company === user.company
    ) {
      return true;
    } else if (user.role === userRoles.RESELLER && test.company === user.resellerId) {
      return true;
    } else if (test.account === user._id || test.publicToCommunity) {
      return true;
    }
    return false;
  };

  const isPublishOrArchiveVisible = () => {
    if (user.role === userRoles.SUPER_ADMIN) {
      return true;
    } else if (
      [userRoles.COMPANY_ADMIN, userRoles.DEPARTMENT_ADMIN, userRoles.EMPLOYEE].includes(user.role) &&
      test.company === user.company
    ) {
      return true;
    } else if (user.role === userRoles.RESELLER && test.company === user.resellerId) {
      return true;
    }
    return false;
  };

  const isEditVisible = useMemo(() => {
    var isCode = [categories.CODE.category, categories.CHALLENGE.category, categories.EXERCISE.category].includes(
      test.category
    );
    if (user.role === userRoles.SUPER_ADMIN && (isCode || test.company === user.adminCompany)) {
      return true;
    } else if (
      [userRoles.COMPANY_ADMIN, userRoles.DEPARTMENT_ADMIN, userRoles.EMPLOYEE].includes(user.role) &&
      test.company === user.company
    ) {
      return true;
    } else if (user.role === userRoles.RESELLER && test.company === user.resellerId) {
      return true;
    } else if (user.role === userRoles.CANDIDATE && test.account === user._id) {
      return true;
    }
    return false;
  }, [test, user]);

  const isQuestionFeedbackVisible = () => {
    if (
      test.category === categories.CODE.category ||
      test.category === categories.CHALLENGE.category ||
      test.category === categories.EXERCISE.category
    )
      return false;
    return user.role === userRoles.SUPER_ADMIN || (test.department && test.department === user.department);
  };

  const isViewImplementationVisible = () => {
    return (
      !isEditVisible &&
      test.publicToCommunity &&
      test.account !== user._id &&
      [categories.CHALLENGE.category, categories.EXERCISE.category].includes(test.category)
    );
  };

  const setPublishOrArchiveTest = (isConfirmed) => {
    setShowConfirmationModal(false);
    if (isConfirmed) {
      if (modalInfo.isPublishOrArchiveModal)
        publishOrArchiveTest({ test: modalInfo.test, operation: modalInfo.operation });
      else copyTest({ _id: modalInfo._id, isMultichoice: modalInfo.isMultichoice });
    }
    setModalInfo({});
  };

  const getEditorUrl = () => {
    if (!isEditVisible) return;
    var isCode = [categories.CODE.category, categories.CHALLENGE.category, categories.EXERCISE.category].includes(
      test.category
    );
    if (isCode) return `${getConfig().REDIRECTION_ROOT_URL}/freecode-test-editor/${test._id}`;
    else return `${getConfig().REDIRECTION_ROOT_URL}/edit-test/${test._id}/`;
  };

  return (
    <CardContainer>
      {showConfirmationModal && (
        <YesNoModal modalInfo={modalInfo} confirm={(isConfirmed) => setPublishOrArchiveTest(isConfirmed)} />
      )}
      <AnchorLink href={getEditorUrl()}>
        <Image image={test.image} />
      </AnchorLink>
      <ChallengeInfo>
        <ChallengeInfoHeader>
          <ChallengeNameAndCategory>
            <ChallengeName title={test.name}>{test.name}</ChallengeName>
            <ChallengeCategory>{test.categoryName}</ChallengeCategory>
          </ChallengeNameAndCategory>
          <ChallengeDetailsAndTestTag>
            <DetailsContainer>
              <ChallengeDetails>
                <DetailsLabel>Created At:&nbsp;</DetailsLabel>
                {moment(test.createdAt).format('ll')}
              </ChallengeDetails>
              <ChallengeDetails>
                <DetailsLabel>Edited At:&nbsp;</DetailsLabel>
                {getEditedAtTime()}
              </ChallengeDetails>
              {test.owner && (
                <ChallengeDetails>
                  <DetailsLabel>Owner:&nbsp;</DetailsLabel>
                  {test.owner}
                </ChallengeDetails>
              )}
              {test.averageRating && (
                <ChallengeDetails>
                  <DetailsLabel>Average Rating:&nbsp;</DetailsLabel>
                  {test.averageRating}
                </ChallengeDetails>
              )}
              {!isNaN(test.totalParticipants) && (
                <ChallengeDetails>
                  <DetailsLabel>Total Participants:&nbsp;</DetailsLabel>
                  {test.totalParticipants}
                </ChallengeDetails>
              )}
            </DetailsContainer>
            <Tags
              isTestTag={true}
              tags={[test.published ? 'Published' : test.custom ? 'Custom' : 'Archived']}
              tagColor={test.published ? colors.green2 : test.custom ? colors.yellow : colors.red}
            />
          </ChallengeDetailsAndTestTag>
        </ChallengeInfoHeader>
        <ButtonContainer>
          {isQuestionFeedbackVisible() && (
            <AnchorLink href={`${getConfig().REDIRECTION_ROOT_URL}/question-feedback/${test._id}`}>
              <Button primaryGreenBorder small fontSize="9px" padding="5px 5px" margin="1px 0 1px 3px" primaryBlue>
                <i className="fa fa-comments-o" style={{ marginRight: '2px' }}></i>Feedback
              </Button>
            </AnchorLink>
          )}
          {isPublishOrArchiveVisible() && (
            <Button
              primaryGreenBorder
              small
              fontSize="9px"
              padding="5px 5px"
              margin="1px 0 1px 3px"
              primaryBlue
              onClick={() => {
                setShowConfirmationModal(true);
                setModalInfo({
                  title: 'Confirmation',
                  body: `Do you want to ${test.archived ? 'publish' : test.custom ? 'publish' : 'archive'} the test?`,
                  operation: test.archived ? 'publishTest' : test.custom ? 'publishTest' : 'archiveTest',
                  test: test._id,
                  isPublishOrArchiveModal: true,
                });
              }}
            >
              <i className="fa fa-bullhorn" style={{ marginRight: '2px' }}></i>
              {test.archived ? 'Publish' : test.custom ? 'Publish' : 'Archive'}
            </Button>
          )}
          {isCopyTestVisible() && (
            <Button
              primaryGreenBorder
              small
              fontSize="9px"
              padding="5px 5px"
              margin="1px 0 1px 3px"
              primaryBlue
              onClick={() => {
                setShowConfirmationModal(true);
                setModalInfo({
                  title: 'Confirmation',
                  body: `Do you want to copy ${test.name}?`,
                  isMultichoice:
                    test.category === categories.MULTICHOICE.category ||
                    test.category === categories.GENERAL_SKILLS.category,
                  _id: test._id,
                });
              }}
            >
              <i className="fa fa-copy" style={{ marginRight: '2px' }}></i>Copy
            </Button>
          )}
          {isEditVisible && (
            <AnchorLink href={getEditorUrl()}>
              <Button primaryGreenBorder small fontSize="9px" padding="5px 5px" margin="1px 0 1px 3px" primaryBlue>
                <i className="fa fa-edit" style={{ marginRight: '2px' }}></i>Edit
              </Button>
            </AnchorLink>
          )}
          {isViewImplementationVisible() && (
            <AnchorLink href={`${getConfig().REDIRECTION_ROOT_URL}/freecode-test-editor/${test._id}`}>
              <Button primaryGreenBorder small fontSize="9px" padding="5px 5px" margin="1px 0 1px 3px" primaryBlue>
                <i className="fa fa-edit" style={{ marginRight: '2px' }}></i>View Implementation
              </Button>
            </AnchorLink>
          )}
        </ButtonContainer>
      </ChallengeInfo>
    </CardContainer>
  );
}
